import { FieldViewModel, Datalayer, DatalayersMap, DatalayerZone, Geometry2, getDistinctValues, getTooltip, IDataLayer, IDatalayerContext, IGeometryInstance, isUndefinedOrNull, LayerTypeId, LocaleString, shell } from "../../omnia"
import { IDatalayerMapDependencies, IDatalayersService } from "../../types"
import { IGenericPlanContext, IZoneEditor } from "../types"
import { AddOperation } from "./operations/add"
import { BufferOperation } from "./operations/buffer/buffer"
import { clipLayerZones } from "./operations/clip"
import { deleteZones } from "./operations/delete"
import { fixGeometryTypes } from "./operations/fix"
import { MergeOperation } from "./operations/merge/merge"
import { createMissingZones, ModifyOperation } from "./operations/modify"
import { NumberOperation } from "./operations/number/number"
import { SplitOperation } from "./operations/split"
import { EditMode } from "./types"
import { validateLayer } from "./validation"

import m = omnia.models
import { ol_Feature, ol_geom, ol_GeometryType } from "@omnia/exports/openlayers"

export class SplittingZoneEditor implements IZoneEditor {

    private topo: omnia.map.IMapTopologyService

    private _dragSelect: Function

    private _layer: Datalayer

    private _editMode: EditMode;

    public modify: ModifyOperation
    public split: SplitOperation
    public merge: MergeOperation
    public add: AddOperation
    public buffer: BufferOperation
    public number: NumberOperation

    private _selectionWatcher: (selection: any) => void
    private _contextMenuHandler: Function

    private _currentZone: DatalayerZone
    private _selectedZones: DatalayerZone[]
    private _currentValue: any
    private _currentLabel: string

    private _multiselect: boolean = false

    public collapseMgmtZones: boolean
    public collapseYieldRecalibration: boolean = false


    public trimTargets: {
        description: string,
        boundary: Geometry2
        vm: FieldViewModel
    }[]


    constructor(
        private $scope: ng.IScope,
        private svc: IDatalayersService,
        private map: DatalayersMap,
        private deps: IDatalayerMapDependencies,
        private _outerGeometry: () => ol_geom.Geometry,
        private readonly onZonesChanged: (zones: DatalayerZone[]) => void,
        private readonly genericPlanContextProvider: () => IGenericPlanContext
    ) {

        this.topo = deps.topology

        this._selectionWatcher = (selection: any) => this.onSelectedFeaturesChanged(selection)

    }

    dispose() {

        this.map.enableMultipleSelection = false
        this.map.unwatchSelection(this._selectionWatcher)
        this.resetContextMenu()

        if (this._dragSelect) {
            this._dragSelect()
            this._dragSelect = null
        }


    }

    public setup(target: IDataLayer, ctxt: IDatalayerContext) {

        if (this._layer) {
            throw new Error("Cannot be reset")
        }

        const layer = <Datalayer>ctxt.createMapLayerInstance(target)

        this.map.addLayer(layer, true)

        ctxt.onZoneValueChanged = zone => this.onZoneValueChanged(<DatalayerZone>zone)

        this._layer = layer
        this.setupMap()

        this.modify = new ModifyOperation(this.map, this.layer, this.topo, this._outerGeometry)
        this.split = new SplitOperation(this.map, this.layer, this.topo)
        this.merge = new MergeOperation(this.map, this.layer, this.topo, () => this._selectedZones)
        this.add = new AddOperation(this.map, this.layer, this.topo, this._outerGeometry)
        this.buffer = new BufferOperation(this.map, this.layer, this.topo, this._outerGeometry, () => this.$scope.$applyAsync())
        this.number = new NumberOperation(this.svc, this.map, this.layer, this._outerGeometry, () => this.$scope.$applyAsync())

        this._dragSelect = omnia.map.createDragSelectInteraction(this.map, layer).dispose

        return layer

    }

    setLayerTargets(field: FieldViewModel, cropping: FieldViewModel, croppingList: FieldViewModel[]) {

        if (!(field && field.hasBoundary())) {
            this.trimTargets = []
            return
        }

        const getOrder = (a: FieldViewModel) => a == cropping ? 0 : a == field ? 1 : 2

        const msgTrimToField = "Trim to the field";

        const cropDescr = (a: FieldViewModel) => a.getCroppingDescription(false, false, true)

        const targets = (croppingList || [])
            .concat([field])
            .filter(t => t.hasBoundary())
            .sort((a, b) => (getOrder(a) - getOrder(b)) || (b.area - a.area) || a.description.localeCompare(b.description))
            .map(a => ({
                vm: a,
                boundary: a.getBoundary(),
                description: a.isField()
                    ? msgTrimToField
                    : `Trim to '${cropDescr(a)}'` as LocaleString
            }))

        this.trimTargets = targets


    }

    private setupMap() {

        const newLayer = this.isLayerNew()

        this.collapseMgmtZones = false
        this.collapseYieldRecalibration = newLayer

        this.layer.selectable = true;
        this.map.setSelectableLayers([this.layer])

        this.map.watchSelection(this._selectionWatcher)

        this.map.editLayer = this.layer

    }

    startEditing() {

        this.resetContextMenu()

        this._contextMenuHandler = this.map.addTooltipProvider(p => this.handleDefaultContextMenu(p))

        fixGeometryTypes(this.layer)

    }

    private handleDefaultContextMenu(tooltip: omnia.map.IMapTooltip) {

        const filter = (l: Datalayer) => l.typeId == this.layer.typeId

        const show = getTooltip(tooltip, filter)

        if (show && tooltip.contextMenu) {

            tooltip.setValue = (v => {
                this.currentValue = v
                this.clearSelectionOnValueChanged()
            })

            const multi = this._multiselect || tooltip.shiftKey
            const selected = (multi ? this.map.getSelectedFeatures() : []) || []

            const { feature } = tooltip

            if (feature && (feature instanceof ol_Feature) && selected.indexOf(feature) < 0) {
                selected.push(feature)
                this.map.selectFeatures(selected)
            }

        }

        return show

    }


    private resetContextMenu() {
        if (this._contextMenuHandler) {
            this._contextMenuHandler()
            this._contextMenuHandler = null
        }
    }

    updateZoneValues() {
        this.layer.refresh()
    }

    get layer() {
        return this._layer;
    }

    get templateUrl(): string {
        return "app/datalayers/edit/splits/editor.html"
    }

    get bodyTemplateUrl() {

        switch (this._editMode) {

            case EditMode.Merge:
                return "app/datalayers/edit/splits/operations/merge/merge.html"

            case EditMode.Buffer:
                return "app/datalayers/edit/splits/operations/buffer/buffer.html"

            case EditMode.Number:
                return "app/datalayers/edit/splits/operations/number/number.html"

            default:

                if (this.multipleSelection) {
                    return "app/datalayers/edit/splits/zone-multiple.html"
                }

                return "app/datalayers/edit/splits/zone.html"

        }

    }

    get valueTemplateUrl(): string {

        let tmpl: string;

        if (this.layer) {

            tmpl = this.layer.valueEditTemplate;
            if (tmpl) return tmpl;

            var type = this.layer.type;

            if (type) {

                if (type.ValueType == m.LayerValueType.Discrete) {
                    return "app/datalayers/edit/splits/value-discrete.html";
                }

                if (type.ValueType == m.LayerValueType.Numeric) {
                    return "app/datalayers/edit/splits/value-numeric.html";
                }

            }

        }

    }

    get zones(): DatalayerZone[] {
        return this.layer.zones;
    }

    isReady() {
        let zones = this.zones;
        return (zones && !!zones.length);
    }

    isEditing(): boolean {
        return this.isDrawing() || (!!this._editMode)
    }

    isDrawing(): boolean {
        return this.map.getDrawingMode() || this.map.getEditMode()
    }

    isMerging(): boolean {
        return (this._editMode == EditMode.Merge)
    }

    isLayerEmpty(): boolean {
        return !this.zones.length;
    }

    isLayerNew() {
        return !(this.layer && this.layer.datalayerId)
    }

    hasMultipleZones(): boolean {
        return this.zones.length > 1
    }

    canChangeCurrentZone(): boolean {
        return !this.isEditing();
    }

    canChangeZoneNumbers() {
        return this.layer.type && (!this.layer.type.ZoneNumbersReadOnly);
    }

    canStop(): boolean {
        return this.isEditing();
    }

    isYieldLayer() {
        return this._layer && (this._layer.typeId == LayerTypeId.Yield)
    }

    get currentZone(): DatalayerZone {
        return this._currentZone;
    }

    get zone(): DatalayerZone {
        return this._currentZone
    }

    set currentZone(value: DatalayerZone) {
        this.setCurrentZone(value, false)
    }

    private initCurrentValue() {

        this._currentValue = null
        this._currentLabel = null

        if (this.zone) {

            this._currentValue = this.zone.value

        }
        else if (this._selectedZones) {

            const values = this._selectedZones.map(v => v.value).filter(v => !isUndefinedOrNull(v))
            const distinct = getDistinctValues(values, v => v)

            if (distinct.length == 1) {
                this._currentValue = distinct[0]
            }
        }

        const planContext = this.genericPlanContextProvider()

        this._currentLabel = planContext && planContext.getLabel(String(this._currentValue))

    }


    get selectedZones(): DatalayerZone[] {
        return this._selectedZones;
    }

    get enableMultiSelect() {
        return this._multiselect
    }

    set enableMultiSelect(value) {
        this._multiselect = value
        this.map.enableMultipleSelection = value
        if (!value) {
            this.map.clearSelection()
        }
    }

    clearSelectionOnValueChanged() {
        if (this.multipleSelection && this.enableMultiSelect) {
            this.map.clearSelection()
        }
    }

    get multipleSelection() {
        return this._selectedZones && (this._selectedZones.length > 1)
    }

    get currentValue() {
        return this._currentValue
    }

    set currentValue(value: number) {

        this._currentValue = value

        if (this._selectedZones) {
            this._selectedZones.forEach(z => z.setValue(value, false))
            this.onZoneValueChanged(null)
        }

    }

    currentValueIsNull() {
        return isUndefinedOrNull(this.currentValue)
    }

    get currentLabel() {
        return this._currentLabel
    }

    set currentLabel(label) {
        this._currentLabel = label
        const planContext = this.genericPlanContextProvider()
        if (planContext) {
            planContext.setLabel(String(this._currentValue), label)
        }
    }

    get supportedValues() {
        return this.layer && this.layer.supportedValues
    }

    isZoneSelected(zone: DatalayerZone): boolean {
        return this.selectedZones && (this.selectedZones.indexOf(zone) >= 0)
    }

    toggleZoneSelection(zone: DatalayerZone) {

        var selected = !this.isZoneSelected(zone);

        if (!selected) {
            if (this._selectedZones) this._selectedZones.remove(z => z == zone);
        }
        else {
            if (this._selectedZones) {
                if (this._selectedZones.indexOf(zone) < 0) this._selectedZones.push(zone);
            }
            else {
                this._selectedZones = [zone];
            }
        }

        this.updateMapSelection();

    }

    selectAllZones() {
        this._selectedZones = this.zones.slice();
        this.updateMapSelection();
    }

    unselectAllZones() {
        this._selectedZones = [];
        this.updateMapSelection();
    }

    reverseZoneSelection() {
        var selection = this.selectedZones || [];
        this._selectedZones = this.zones.filter(z => selection.indexOf(z) < 0);
        this.updateMapSelection();
    }

    private updateMapSelection() {
        var sz = this._selectedZones ? this._selectedZones.map(z => z.feature) : [];
        this.map.selectFeatures(sz);
    }


    canModify() {
        if (this.multipleSelection) return false
        if (this.currentZone) return true
        if (this.zones.length == 1) return true
        return false
    }

    canDeleteZones() {
        if (this.isEditing()) return false
        if (!this._selectedZones) return false
        if (!this._selectedZones.length) return false
        return true
    }

    startModify() {

        const zone = this._currentZone

        if (!this.commitCurrent()) return

        this.map.unwatchSelection(this._selectionWatcher)
        this.map.clearSelection()

        this.map.autoEdit({ layer: this.layer, watcher: (feature: ol_Feature) => this.onEditedFeatureChanged(feature) })

        this.modify.start(zone)
        this._editMode = EditMode.Modify

    }

    canMerge() {
        return this.hasMultipleZones()
    }

    startMerge() {

        if (!this.commitCurrent()) return

        this.merge.start()
        this._editMode = EditMode.Merge

    }

    startAddZone(type: ol_GeometryType = "Polygon") {

        if (!this.commitCurrent()) return

        this.add.start(type)
        this._editMode = EditMode.Add

    }

    startMargin() {

        if (!this.commitCurrent()) return

        this.buffer.start()
        this._editMode = EditMode.Buffer

    }

    startNumbering() {

        if (!this.commitCurrent()) return

        this.number.start()
        this._editMode = EditMode.Number

    }

    deleteZones() {

        if (!this.canDeleteZones()) return

        const msg = "Do you really want to delete the selected zones?"
        const title = "Delete zones"

        shell.showYesNo(this.deps.shell, msg, title).then(ok => {

            if (ok) {

                if (!deleteZones(this.layer, this._selectedZones, this.topo)) {

                    createMissingZones(this.layer, this.topo, this._outerGeometry())

                }

                this.map.clearSelection()

            }

        })

    }

    canSave(): boolean {
        if (!this.canCommitCurrent()) return false
        if (this.isMerging()) return false
        if (!this.layer.zones.length) return false
        return true
    }

    canCommitCurrent(): boolean {

        if (this._currentZone) {
            if (!this._currentZone.zoneNumber) {
                return false
            }
        }

        switch (this._editMode) {

            case EditMode.Merge:
                return this.merge.canCommit()

            case EditMode.Buffer:
                return this.buffer.canCommit()

            case EditMode.Number:
                return this.number.canCommit()

            default:
                return this.map.canCommit()

        }

    }

    cancelCurrent() {

        this.map.cancelEdit();

        if (this._editMode == EditMode.Modify) {
            this.zones.forEach(z => z.cancelChanges());
        }

        this.cleanupEditMode();

    }

    commitCurrent(): boolean {

        if (!this.canCommitCurrent()) return false;

        const zone = this.currentZone

        let ok = true

        switch (this._editMode) {

            case EditMode.Merge:
                ok = this.merge.commit()
                break

            case EditMode.Modify:
                ok = this.modify.commit(zone)
                break

            case EditMode.Split:
                ok = this.split.commit()
                break

            case EditMode.Add:
                ok = this.add.commit()
                break

            case EditMode.Buffer:
                ok = this.buffer.commit()
                break

            case EditMode.Number:
                ok = this.number.commit()
                break

        }

        if (ok) {

            this.cleanupEditMode()

            if (zone && this.onZonesChanged) {
                this.onZonesChanged([zone])
            }

        }

        return ok

    }

    private onEditedFeatureChanged(feature: any): boolean {

        const zone = this.layer.zones.find(z => z.feature == feature);

        if (this._currentZone != zone) {
            this.setCurrentZone(zone, true)
        }

        if (this.onZonesChanged) {
            this.onZonesChanged([zone])
        }

        return true

    }

    setCurrentZone(zone: DatalayerZone, apply: boolean = false) {

        if ((this._editMode != EditMode.Modify) && zone.feature) {
            this.map.selectFeatures([zone.feature]);
        }

        this._currentZone = zone
        this._selectedZones = [zone]
        this.initCurrentValue()

        if (zone) {
            zone.setInitialState()
            this.toggleMgmtZones(false)
        }

        if (this.isEditing()) {
            this.modify.notifyZoneChanged(zone)
        }

        if (apply) {
            this.$scope.$applyAsync();
        }

    }

    startSplit() {

        if (!this.commitCurrent()) return

        this.split.start()
        this._editMode = EditMode.Split

    }

    private cleanupEditMode() {

        if (this._editMode == EditMode.Merge) {
            this.map.clearSelection()
        }

        this.modify.stop()
        this.split.stop()
        this.merge.stop()
        this.add.stop()
        this.buffer.stop()
        this.number.stop()

        this.zones.forEach(z => z.commitState())

        this._editMode = null

        this.map.autoEdit(null)

        this.map.watchSelection(this._selectionWatcher)

    }

    private onSelectedFeaturesChanged(features: any[]) {

        this._selectedZones = (features ? features.map(f => this.layer.zones.find(z => z.feature == f)).filter(z => !!z) : [])
        this._currentZone = (this._selectedZones.length == 1 ? this._selectedZones[0] : null)
        this.initCurrentValue()

        if (this._currentZone) {
            this.toggleMgmtZones(false)
        }

        this.merge.notifySelectionChanged()

    }


    onZoneNumberChanged(zone: DatalayerZone) {
        this.layer.refresh()
        this.map.refresh()
    }

    onZoneValueChanged(zone: DatalayerZone) {

        zone = zone || this._currentZone
        const zones = zone ? [zone] : this._selectedZones

        this.layer.refresh()
        this.map.refresh()

        if (this.canCommitCurrent() && zone && (zone != this._currentZone)) {
            this.setCurrentZone(zone, false)
        }

        if (zones && zones.length && this.onZonesChanged) {
            this.onZonesChanged(zones)
        }

    }

    validateLayer() {
        return validateLayer(this.layer, this.deps.shell)
    }

    public toggleMgmtZones(collapse?: boolean) {

        collapse = isUndefinedOrNull(collapse) ? !this.collapseMgmtZones : collapse
        this.collapseMgmtZones = collapse

        if (collapse) {
            this.map.clearSelection()
        }

    }

    trimToBoundary(boundary: IGeometryInstance) {

        clipLayerZones(this.layer, this.topo, boundary)

    }

}
